import React, { memo, useState } from 'react';
import { NodeProps, Node, Handle, Position } from '@xyflow/react';
import { DatabaseNodeData } from '../types/diagram.types';

type DatabaseNodeType = Node<DatabaseNodeData, 'database'>;

const DB_ICONS: Record<string, string> = {
  PostgreSQL: '\uD83D\uDC18', Redis: '\uD83D\uDD34', TimescaleDB: '\uD83D\uDCC8', S3: '\uD83E\uDEA3',
};

const DB_COLORS: Record<string, string> = {
  PostgreSQL: '#303F9F', Redis: '#D32F2F', TimescaleDB: '#00796B', S3: '#F9A825',
};

const DatabaseNode = memo(({ data, selected }: NodeProps<DatabaseNodeType>) => {
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const dbColor = DB_COLORS[data.dbType] ?? '#546E7A';
  const dbIcon = DB_ICONS[data.dbType] ?? '\uD83D\uDDC4\uFE0F';

  const toggleTable = (tableName: string) => {
    setExpandedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableName)) next.delete(tableName);
      else next.add(tableName);
      return next;
    });
  };

  return (
    <div style={{
      background: '#fff',
      border: selected ? `2px solid ${dbColor}` : `1.5px solid ${dbColor}55`,
      borderRadius: 8,
      minWidth: 200,
      maxWidth: 280,
      fontFamily: 'system-ui, sans-serif',
      boxShadow: selected ? `0 4px 12px ${dbColor}33` : '0 1px 4px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      transition: 'box-shadow 0.15s',
    }}>
      {/* Header */}
      <div style={{ background: dbColor, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 14, lineHeight: 1 }}>{dbIcon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#fff', fontSize: 11, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {data.dbName}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 9 }}>{data.dbType}</div>
        </div>
        {data.connected ? (
          <span style={{ fontSize: 9, background: '#4CAF50', color: '#fff', padding: '1px 5px', borderRadius: 3 }}>&bull; live</span>
        ) : (
          <span style={{ fontSize: 9, background: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', padding: '1px 5px', borderRadius: 3 }}>static</span>
        )}
      </div>

      {/* Tables */}
      <div style={{ padding: '4px 0 4px', maxHeight: 300, overflowY: 'auto' }}>
        {data.tables.map(table => {
          const isExpanded = expandedTables.has(table.name);
          return (
            <div key={table.name}>
              <button
                onClick={e => { e.stopPropagation(); toggleTable(table.name); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  width: '100%', padding: '4px 10px',
                  background: isExpanded ? `${dbColor}10` : 'transparent',
                  border: 'none', cursor: 'pointer',
                  borderLeft: isExpanded ? `3px solid ${dbColor}` : '3px solid transparent',
                  transition: 'background 0.1s',
                }}
              >
                <span style={{ fontSize: 9, color: '#757575', width: 10 }}>{isExpanded ? '\u25BE' : '\u25B8'}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#212121' }}>{table.name}</span>
                <span style={{ fontSize: 9, color: '#9E9E9E', marginLeft: 'auto' }}>
                  {table.columns.length} cols
                </span>
              </button>

              {isExpanded && (
                <div style={{ padding: '2px 0 4px 22px', background: '#FAFAFA', borderLeft: `3px solid ${dbColor}55` }}>
                  {table.columns.map(col => (
                    <div key={col.name} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '1px 8px 1px 0', fontSize: 10 }}>
                      {col.pk && <span style={{ fontSize: 9, color: '#F9A825' }} title="Primary Key">\uD83D\uDD11</span>}
                      {col.fk && <span style={{ fontSize: 9, color: '#2196F3' }} title={`FK \u2192 ${col.fk}`}>\uD83D\uDD17</span>}
                      {!col.pk && !col.fk && <span style={{ width: 13 }} />}
                      <span style={{ fontWeight: col.pk ? 700 : 400, color: col.pk ? '#212121' : '#424242', minWidth: 70, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {col.name}
                      </span>
                      <span style={{ color: '#9E9E9E', fontSize: 9, marginLeft: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {col.type}
                      </span>
                      {col.notes && (
                        <span title={col.notes} style={{ color: '#BDBDBD', fontSize: 9, marginLeft: 'auto' }}>\u2139\uFE0F</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Handles */}
      <Handle type="target" position={Position.Top} style={{ background: dbColor, border: '2px solid #fff', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: dbColor, border: '2px solid #fff', width: 8, height: 8, opacity: 0.5 }} />
    </div>
  );
});

DatabaseNode.displayName = 'DatabaseNode';
export default DatabaseNode;
